/**
 * Is this access token still valid by its own `exp` claim?
 *
 * The session used to be considered live whenever a token STRING was present,
 * regardless of age. A tab left open past the token's lifetime therefore booted
 * "authorized", was sent to /office, got 401s on every call, was signed out by
 * the interceptor, and bounced back to /auth — where the layout could send it
 * to /office again. The result was a redirect ping-pong that locks the page up
 * with a spinner and no error.
 *
 * Decoding is deliberately defensive: a token we cannot read is treated as
 * expired, because landing on the login screen is always recoverable whereas
 * a wedged session is not.
 */
export function isTokenExpired(token: string | null | undefined, skewSeconds = 30): boolean {
  if (!token) {
    return true;
  }

  const payload = decodePayload(token);
  if (payload === null) {
    // Not a token we can read at all — treat it as unusable rather than
    // hoping. This is the case that used to slip through as "still valid".
    return true;
  }

  if (typeof payload.exp !== 'number') {
    // Readable, but carries no expiry claim: we cannot prove it is stale,
    // so let the API be the judge.
    return false;
  }
  const exp = payload.exp;

  // Treat a token that is about to expire as already expired, so we do not
  // start a page load that will 401 halfway through.
  return Date.now() >= (exp - skewSeconds) * 1000;
}

function decodePayload(token: string): { exp?: number } | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) {
      return null;
    }
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}
